import { CurrencyFormatter } from "_helpers/formatters";

const emptyDoughnutPlugin = {
  id: "emptyDoughnut",
  afterDraw(chart, args, options) {
    const { datasets } = chart.data;
    const { color, width, radiusDecrease } = options;
    let hasData = false;

    for (let i = 0; i < datasets.length; i += 1) {
      const dataset = datasets[i];
      hasData |= dataset.data.length > 0;
    }

    if (!hasData) {
      const {
        chartArea: { left, top, right, bottom },
        ctx,
      } = chart;
      const centerX = (left + right) / 2;
      const centerY = (top + bottom) / 2;
      const r = Math.min(right - left, bottom - top) / 2;

      ctx.beginPath();
      ctx.lineWidth = width || 2;
      ctx.strokeStyle = color || "rgba(255, 128, 0, 0.5)";
      ctx.arc(centerX, centerY, r - radiusDecrease || 0, 0, 2 * Math.PI);
      ctx.stroke();
    }
  },
};

const showLabelsInsidePlugin = {
  id: "showLabelsInside", // Unique identifier for the plugin
  afterDatasetsDraw: function (chart) {
    const ctx = chart.ctx;
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);

      meta.data.forEach((bar, index) => {
        const value = dataset.data[index];

        // Set the font and color for the label text
        ctx.font = "bold 14px Arial";
        ctx.fillStyle = "white"; // Text color (change as needed)
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";

        // Position the label inside the bar
        const x = datasetIndex === 0
          ? bar.base + 10  // Dataset 0 (Blue): Start of bar
          : bar.base + bar.width - 10; // Dataset 1 (Green): End of bar

        ctx.textAlign = datasetIndex === 0 ? "left" : "right";

        const y = bar.y; // Vertical center of the bar
        if (bar.width > 0)
          ctx.fillText(`${CurrencyFormatter(value / 1000)}K`, x, y); // Draw the label (value) inside the bar
      });
    });
  },
};

const ChartDatasetLabelsPlugin = {
  id: "datasetLabels",
  afterDraw(chart) {
    const { ctx, chartArea, data } = chart;
    const { left, right, top, bottom } = chartArea;
    const centerX = (left + right) / 2;
    const centerY = (top + bottom) / 2;

    ctx.font = "bold 12px Arial";
    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.strokeStyle = "#FFFFFF"; // Set line color to white
    ctx.lineWidth = 2;

    data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.data.length > 0) {
        // Get outer radius of any data point in this dataset (all will have the same radius)
        const outerRadius = meta.data[0].outerRadius;
        //datasetOuterRadii.push({ dataset: dataset.label, outerRadius });

        const angle = (90 * Math.PI) / 180;

        // Calculate the starting point at the outer radius of the ring
        const xStart = centerX + Math.cos(angle) * outerRadius;
        const yStart = centerY + Math.sin(angle) * outerRadius;

        // Draw the label at the outer end of the line
        const label = dataset.label; // Use the dataset label
        ctx.fillText(label, xStart, yStart - 5);
      }
    });
  },
};

const ChartDatasetLinesPlugin = {
  id: "datasetLines",
  afterDraw(chart) {
    const { ctx, chartArea, data } = chart;
    const { left, right, top, bottom } = chartArea;
    const centerX = (left + right) / 2;
    const centerY = (top + bottom) / 2;

    ctx.font = "bold 18px Arial";
    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.strokeStyle = "#FFFFFF"; // Set line color to white
    ctx.lineWidth = 2;
    let datasetOuterRadii = [];
    const angles = [0, 60, 120, 180, 240];

    data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.data.length > 0) {
        // Get outer radius of any data point in this dataset (all will have the same radius)
        const outerRadius = meta.data[0].outerRadius;
        datasetOuterRadii.push({ dataset: dataset.label, outerRadius });

        // Calculate offset dynamically based on the outer radius (inverse relation)
        const maxRadius = 200; // Max possible radius (you can adjust this based on your chart size)
        const minOffset = 100; // Minimum offset
        const maxOffset = 200; // Maximum offset

        // Map outer radius to offset (inverse relationship)
        const offset =
          maxOffset - (outerRadius / maxRadius) * (maxOffset - minOffset);
        // Convert angle to radians
        const angle = (angles[datasetIndex] * Math.PI) / 180;

        //const angle = (90 * Math.PI) / 180;

        // Calculate the starting point at the outer radius of the ring
        const xStart = centerX + Math.cos(angle) * outerRadius;
        const yStart = centerY + Math.sin(angle) * outerRadius;

        // Calculate the end point by extending the line outward from the ring
        const xEnd = centerX + Math.cos(angle) * (outerRadius + offset);
        const yEnd = centerY + Math.sin(angle) * (outerRadius + offset);

        // Draw the line from the center to the calculated point
        ctx.beginPath();
        ctx.moveTo(xStart, yStart);
        ctx.lineTo(xEnd, yEnd);
        ctx.stroke();

        // Draw the label at the outer end of the line
        const label = dataset.label; // Use the dataset label
        const labelWidth = ctx.measureText(label).width; // Get width of the label text
        ctx.fillText("label", xEnd + 20 > xEnd ? xEnd + 20 : xEnd - 20, yEnd); // Center the label on the line

        //ctx.fillText(label, xStart, yStart - 10);
      }
    });
  },
};

export {
  emptyDoughnutPlugin,
  showLabelsInsidePlugin,
  ChartDatasetLinesPlugin,
  ChartDatasetLabelsPlugin,
};
